import { __decorate, __metadata } from 'tslib';
import * as i0 from '@angular/core';
import { booleanAttribute, Component, ViewEncapsulation, ChangeDetectionStrategy, Input } from '@angular/core';
import { CSSModifier, CSSComponent } from '@odx/angular/internal';
import { createConfigTokens, injectElement } from '@odx/angular/utils';

const IconSet = {
    CORE: 'core',
    MEDICAL: 'medical',
    SAFETY: 'safety',
    UIB_LEGACY: 'uib-legacy',
};

const IconSize = {
    SMALL: 'small',
    MEDIUM: 'medium',
    LARGE: 'large',
    XLARGE: 'xlarge',
    INLINE: 'inline',
};

/**
 * Unpacks a combined icon identifier string into separate icon set and icon name components.
 * The identifier is expected to follow the format "iconSet::iconName". If the "iconName" part is
 * missing, the function assumes the icon belongs to the 'core' set.
 *
 * @param {string} value The combined identifier string in the format "iconSet::iconName".
 * @returns {[iconSet: string, iconName: string]} A tuple containing the icon set and icon name as two separate strings. Defaults to 'core'
 *          if the iconName is omitted in the input.
 *
 * @example
 * ```ts
 * unpackIconIdentifier("menu")       // returns ['core', 'menu']
 * ```
 */
function unpackIconIdentifier(value) {
    const [iconSet, iconName] = value.split('::');
    if (!iconName)
        return [IconSet.CORE, iconSet];
    const set = isIconSet(iconSet) ? iconSet : IconSet.CORE;
    return [set, iconName];
}
/**
 * Determines if a given string is a valid icon set.
 *
 * @param {string} value The value to check if it is a valid icon set.
 * @returns {boolean} Whether the value is a valid icon set.
 *
 * @example
 * ```ts
 * isIconSet('core')       // returns true
 * isIconSet('custom')     // returns false
 * ```
 */
function isIconSet(value) {
    return Object.values(IconSet).includes(value);
}

/**
 * Utility functions generated to handle the injection and provision of the `IconConfig`.
 * These include tokens and functions for accessing and providing icon configurations.
 *
 * - `IconConfig`: A type representing the shape of the icon configuration object.
 * - `IconDefaultConfig`: The default configuration which sets 'core' as the default icon set.
 * - `injectIconConfig`: A function to inject the icon configuration, usable in Angular components or services.
 * - `provideIconConfig`: A function to provide the icon configuration, typically used in Angular module providers.
 *
 * @example
 * ```ts
 * // To inject icon configuration in a component or service
 * const config = injectIconConfig();
 * console.log(config.defaultIconSet); // Outputs: 'core'
 *
 * // To provide icon configuration in a module
 * provideIconConfig({ defaultIconSet: 'custom' });
 * ```
 */
const { IconConfig, IconDefaultConfig, injectIconConfig, provideIconConfig } = createConfigTokens('Icon', '@odx/angular/components/icon', {
    defaultIconSet: IconSet.CORE,
});

/**
 * Represents an icon component that can dynamically display various icons based on input properties. This component
 * leverages CSS for styling and is designed to be easily embedded within other components and views.
 *
 * It allows for specifying icon sets and individual icon names, supporting flexible usage across different contexts
 * and themes. The component is also optimized for performance with Angular's OnPush change detection strategy.
 */
let IconComponent = class IconComponent {
    constructor() {
        this.element = injectElement();
        /**
         * Determines whether the icon should be displayed inline with text or other elements.
         *
         * @type {boolean}
         * @default false
         */
        this.inline = false;
        /**
         * Specifies the size of the icon, which can be adjusted using predefined sizes from the IconSize enum.
         *
         * @type {IconSize}
         * @default IconSize.MEDIUM
         */
        this.size = IconSize.MEDIUM;
        /**
         * The name of the icon to be displayed, which corresponds to specific icons within a given icon set.
         *
         * @type {string | null}
         * @default null
         */
        this.name = null;
        /**
         * Specifies the icon set that the icon belongs to. The default set can be injected from the icon configuration.
         *
         * @type {IconSet | null}
         */
        this.iconSet = injectIconConfig().defaultIconSet;
    }
    /**
     * Allows for setting both the icon set and icon name via a single identifier, typically formatted as 'set::name'.
     * Unpacking this identifier sets both `name` and `iconSet` properties.
     *
     * @param {string | null | undefined} value - The identifier to unpack.
     */
    set identifier(value) {
        const [iconSet, name] = value ? unpackIconIdentifier(value) : [null, null];
        this.name = name;
        this.iconSet = iconSet;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: IconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "16.1.0", version: "18.2.13", type: IconComponent, isStandalone: true, selector: "odx-icon", inputs: { inline: ["inline", "inline", booleanAttribute], size: "size", name: "name", iconSet: ["iconSet", "iconSet", (value) => (isIconSet(value) ? value : null)], identifier: "identifier" }, host: { attributes: { "translate": "no" }, properties: { "class.notranslate": "true", "attr.data-icon-name": "name", "attr.data-icon-set": "iconSet" } }, ngImport: i0, template: '<span></span>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
__decorate([
    CSSModifier(),
    __metadata("design:type", Object)
], IconComponent.prototype, "inline", void 0);
__decorate([
    CSSModifier(),
    __metadata("design:type", String)
], IconComponent.prototype, "size", void 0);
IconComponent = __decorate([
    CSSComponent('icon')
], IconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: IconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'odx-icon',
                    template: '<span></span>',
                    encapsulation: ViewEncapsulation.None,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    standalone: true,
                    host: {
                        '[class.notranslate]': 'true',
                        '[attr.data-icon-name]': 'name',
                        '[attr.data-icon-set]': 'iconSet',
                        translate: 'no',
                    },
                }]
        }], propDecorators: { inline: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], size: [{
                type: Input
            }], name: [{
                type: Input
            }], iconSet: [{
                type: Input,
                args: [{ transform: (value) => (isIconSet(value) ? value : null) }]
            }], identifier: [{
                type: Input
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { IconComponent, IconConfig, IconDefaultConfig, IconSet, IconSize, injectIconConfig, isIconSet, provideIconConfig, unpackIconIdentifier };
